import React, { useContext, useRef, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { addRecord } from "../../firebase/docs";
import ClickAwayListener from "react-click-away-listener";
import DatePicker from "react-datepicker";
import UserContext from "../../context/user/UserContext";
import PiggyContext from "../../context/piggy/PiggyContext";
import "react-datepicker/dist/react-datepicker.css";

function AddRecord() {
  const sourceRef = useRef();
  const amountRef = useRef();
  const dateRef = useRef();

  const userContext = useContext(UserContext);
  const piggyContext = useContext(PiggyContext);

  const [sourceErrMsg, setSourceErrMsg] = useState(null);
  const [amountErrMsg, setAmountErrMsg] = useState(null);
  const [dateErrMsg, setDateErrMsg] = useState(null);

  const [modal, setModal] = useState(false);
  const [source, setSource] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date());
  const [isIncrease, setIsIncrease] = useState(true);

  const handleModalClose = () => {
    setIsIncrease(true);
    setDate(new Date());
    setAmount("");
    setSource("");
    setSourceErrMsg(null);
    setAmountErrMsg(null);
    setDateErrMsg(null);
    setModal(false);
  };
  const handleClickAway = () => {
    if (modal) return handleModalClose();
  };
  const handleModalSubmit = async (e) => {
    e.preventDefault();

    // SOURCE
    // If source contain any char other than alphanumeric characters or spaces
    if (!/^[a-z0-9]+$/gi.test(source.replaceAll(" ", ""))) {
      setSourceErrMsg("Source should be alphanumeric");
      sourceRef.current.select();
      return;
    }
    // If the source is empty or filled with whitespace
    else if (!source) {
      setSourceErrMsg("Source can't remain empty");
      sourceRef.current.focus();
      return;
    }
    // If no err in source
    else {
      setSourceErrMsg(null);
    }

    // AMOUNT
    // If amount is empty
    if (!amount) {
      setAmountErrMsg("Amount can't remain empty");
      amountRef.current.focus();
      return;
    }
    // If amount is a negative integer
    else if (amount.startsWith("-")) {
      setAmountErrMsg("Select withdrawal instead of -ve integer");
      amountRef.current.select();
      return;
    }
    // If amount contains + or - symbols
    else if (amount.includes("+") || amount.includes("-")) {
      setAmountErrMsg("Amount can't contain + or -");
      amountRef.current.select();
      return;
    }
    // If no err in amount
    else {
      setAmountErrMsg(null);
    }

    // DATE
    // If date not defined
    if (!date) {
      setDateErrMsg("Date can't remain empty");
      dateRef.current.focus();
      return;
    }
    // If no err in date
    else {
      setDateErrMsg(null);
    }

    const newRecord = await addRecord(
      source,
      parseFloat(amount),
      isIncrease,
      Timestamp.fromDate(new Date(date)),
      userContext.user.uid
    );

    piggyContext.setRows((prev) => [newRecord, ...prev]);
    return handleModalClose();
  };

  return (
    <>
      <div className="sticky bottom-0 z-10 bg-white pb-3">
        <button
          onClick={() => setModal(true)}
          className="font-regular md:text-md mt-3 w-full rounded-md bg-gradient-to-r from-sky-500 to-indigo-500 p-2 text-center text-sm font-semibold text-white"
        >
          Add Data
        </button>
      </div>

      {modal && (
        <div className="h-modal fixed top-0 right-0 left-0 z-50 flex w-full items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-20 shadow-md md:inset-0 md:h-full">
          <div className="relative h-full w-full max-w-md p-4 md:h-auto">
            <ClickAwayListener onClickAway={handleClickAway}>
              <div className="relative rounded-lg bg-white shadow">
                <button
                  onClick={handleModalClose}
                  className="absolute top-3 right-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900"
                >
                  <svg
                    aria-hidden="true"
                    className="h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>

                <div className="py-6 px-6 lg:px-8">
                  <h3 className="mb-4 text-xl font-medium text-gray-900">
                    Add a new record
                  </h3>

                  <form className="mx-auto grid max-w-screen-md gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-2 inline-block text-sm text-gray-800 sm:text-base">
                        Source
                      </label>
                      <input
                        ref={sourceRef}
                        type="text"
                        className={`${
                          sourceErrMsg ? "ring-red-300" : "ring-indigo-300"
                        } w-full rounded border bg-gray-50 px-3 py-2 text-gray-800 outline-none transition duration-100 focus:ring`}
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        autoFocus={true}
                      />
                    </div>

                    <div>
                      <label className="mb-2 inline-block text-sm text-gray-800 sm:text-base">
                        Amount
                      </label>
                      <input
                        ref={amountRef}
                        type="number"
                        value={amount}
                        className={`${
                          amountErrMsg ? "ring-red-300" : "ring-indigo-300"
                        } w-full rounded border bg-gray-50 px-3 py-2 text-gray-800 outline-none transition duration-100 focus:ring`}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="mb-2 inline-block text-sm text-gray-800 sm:text-base">
                        Status
                      </label>
                      <div className="flex justify-between">
                        <span
                          onClick={() => setIsIncrease(true)}
                          className={`${
                            isIncrease
                              ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                              : "text-gray-800"
                          }    w-[49.5%] rounded-l-2xl p-2 text-center text-sm`}
                        >
                          Deposit
                        </span>
                        <span
                          onClick={() => setIsIncrease(false)}
                          className={`${
                            !isIncrease
                              ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                              : "text-gray-800"
                          }   w-[49.5%] rounded-r-2xl p-2 text-center text-sm text-white`}
                        >
                          Withdraw
                        </span>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-2 inline-block text-sm text-gray-800 sm:text-base">
                        Date
                      </label>
                      <DatePicker
                        ref={dateRef}
                        selected={date}
                        popperPlacement="top-end"
                        dateFormat="MMMM d, yyyy"
                        onChange={(date) => setDate(date)}
                        className={`${
                          dateErrMsg ? "ring-red-300" : "ring-indigo-300"
                        } w-full rounded border bg-gray-50 px-3 py-2 text-gray-800 outline-none transition duration-100 focus:ring`}
                      />
                    </div>

                    <div className="flex flex-col sm:col-span-2">
                      <span className="text-sm text-red-500">
                        {sourceErrMsg || amountErrMsg || dateErrMsg}
                      </span>
                      <button
                        type="submit"
                        onClick={handleModalSubmit}
                        className="mt-2 inline-block rounded-lg bg-indigo-500 px-8 py-3 text-center text-sm font-semibold text-white outline-none ring-indigo-300 transition duration-100 hover:bg-indigo-600 focus-visible:ring active:bg-indigo-700 disabled:bg-red-800 md:text-base"
                      >
                        Add Record
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </ClickAwayListener>
          </div>
        </div>
      )}
    </>
  );
}

export default AddRecord;
